/**
 * Pixel-Proximity Clustering Engine
 *
 * Groups markers that visually overlap on screen (within `radiusPx` pixels of each other).
 * Only markers that are EXPLICITLY grouped together get flagged.
 *
 * @param {Map} markerPool  - Map of key → { marker, ... }
 * @param {object} projection - Google Maps MapCanvasProjection
 * @param {number} radiusPx - pixel radius threshold for grouping (e.g. 36)
 * @param {Set} eligibleKeys - String(key) set of markers that should participate (i.e. not
 *                             filtered out by the caller). Current marker visibility is
 *                             deliberately ignored so clustering never feeds back on itself.
 * @returns {{ clusters: Array<{keys, centroidLat, centroidLng, count}>, clusteredKeys: Set }}
 */
export function computePixelClusters(markerPool, projection, radiusPx, eligibleKeys) {
    if (!projection) return { clusters: [], clusteredKeys: new Set() };

    const radiusSq = radiusPx * radiusPx;

    // Build list of { key, px, py, lat, lng } for every eligible, on-screen marker.
    const items = [];
    markerPool.forEach((entry, key) => {
        const marker = entry?.marker;
        if (!marker) return;
        if (eligibleKeys && !eligibleKeys.has(String(key))) return;

        const pos = marker.getPosition();
        if (!pos) return;
        const pixel = projection.fromLatLngToContainerPixel(pos);
        if (!pixel) return;
        items.push({ key, px: pixel.x, py: pixel.y, lat: pos.lat(), lng: pos.lng() });
    });

    const n = items.length;
    if (n < 2) return { clusters: [], clusteredKeys: new Set() };

    const clusterList = [];

    // Grid-accelerated sweep: clusters are bucketed into radius-sized cells, so each item only
    // checks the 3x3 neighbouring cells instead of every cluster (O(n) instead of O(n*clusters)).
    const cell = Math.max(radiusPx, 1);
    const grid = new Map();
    const cellKey = (cx, cy) => cx + ',' + cy;
    const addToGrid = (cl) => {
        cl.gk = cellKey(Math.floor(cl.cx / cell), Math.floor(cl.cy / cell));
        let arr = grid.get(cl.gk);
        if (!arr) { arr = []; grid.set(cl.gk, arr); }
        arr.push(cl);
    };
    const removeFromGrid = (cl) => {
        const arr = grid.get(cl.gk);
        if (arr) arr.splice(arr.indexOf(cl), 1);
    };

    for (let i = 0; i < n; i++) {
        const it = items[i];
        const gx = Math.floor(it.px / cell);
        const gy = Math.floor(it.py / cell);
        let best = null;
        let bestD = Infinity;
        for (let ox = -1; ox <= 1; ox++) {
            for (let oy = -1; oy <= 1; oy++) {
                const arr = grid.get(cellKey(gx + ox, gy + oy));
                if (!arr) continue;
                for (let c = 0; c < arr.length; c++) {
                    const cl = arr[c];
                    const dx = it.px - cl.cx;
                    const dy = it.py - cl.cy;
                    const d = dx * dx + dy * dy;
                    if (d <= radiusSq && d < bestD) { best = cl; bestD = d; }
                }
            }
        }
        if (best) {
            removeFromGrid(best);
            const newCount = best.keys.length + 1;
            best.keys.push(it.key);
            best.cx = (best.cx * (newCount - 1) + it.px) / newCount;
            best.cy = (best.cy * (newCount - 1) + it.py) / newCount;
            best.latSum += it.lat;
            best.lngSum += it.lng;
            addToGrid(best);
        } else {
            const cl = { keys: [it.key], cx: it.px, cy: it.py, latSum: it.lat, lngSum: it.lng, gk: '' };
            clusterList.push(cl);
            addToGrid(cl);
        }
    }

    const resultClusters = [];
    const newClusteredKeys = new Set();

    clusterList.forEach((cl) => {
        if (cl.keys.length >= 2) {
            cl.keys.forEach(k => newClusteredKeys.add(String(k)));
            resultClusters.push({
                keys: cl.keys,
                count: cl.keys.length,
                centroidLat: cl.latSum / cl.keys.length,
                centroidLng: cl.lngSum / cl.keys.length,
            });
        }
    });

    return { clusters: resultClusters, clusteredKeys: newClusteredKeys };
}
